import { useEffect, useMemo, useRef, useState } from "react";
import { FiFilter } from "react-icons/fi";
import Badge from "../../../../../components/badge/badge";
import SectionHeader from "../../../../../components/sectionHeader/sectionHeader";
import InputSearch from "../../../../../components/inputs/inputSearch/inputSearch";
import Button from "../../../../../components/button/button";
import Table from "../../../../../components/table/table";
import { getApiInstance } from "../../../../../utils/api";
import Modal from "../../../../../components/modal/modal";
import InputCheckbox from "../../../../../components/inputs/inputCheckbox/inputCheckbox";
import Input from "../../../../../components/inputs/input/input";
import Notification from "../../../../../components/notification/notification";
import Accordion from "../../../../../components/accordion/accordion";
import Tag from "../../../../../components/tag/tag";
import InputDropdown from "../../../../../components/inputs/inputDropdown/inputDropdown";
import InputDropdownAdd from "../../../../../components/inputs/inputDropdownAdd/inputDropdownAdd";
import { matchesSearchInObject } from "../../../../../utils/searchUtils";

const familiaMapping = {
    1: "Mobiliario Central",
    2: "Mobiliario Perimetral",
    3: "Mesas",
    4: "Probadores",
    5: "Caja",
    6: "Dotación",
    7: "Instalaciones",
    8: "Señalética",
    9: "BOH",
};

const categoriasMapping = {
    TiendasTop: "Tiendas Top",
    TiendasLight: "Tiendas Light",
    TiendasLow: "Tiendas Low",
    General: "General",
    Banner: "Banners",
    Senalizacon: "Señalización",
    PositiveStore: "Positive Store",
    MakeUp: "Make Up 400",
};

const estadoLabels = {
    Publicado: "Publicado",
    Borrador: "Borrador",
    Archivado: "Archivado",
    PendienteDeAprobacion: "Pendiente de Aprobación",
};

const columns = [
    {
        key: "rutaImagen",
        label: "Imagen",
        render: (row) => (
            <img
                src={row.rutaImagen || "defaultImage.png"}
                alt={row.nombreBook || "Sin nombre"}
                style={{
                    maxWidth: "48px",
                    maxHeight: "48px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            />
        ),
    },
    { key: "nombreBook", label: "Nombre" },
    { key: "referencia", label: "Referencia" },
    { key: "nombreFamilia", label: "Familia" },
    {
        key: "variante",
        label: "Variante",
        render: (row) => row.variante || "N/A",
    },
    { key: "medida", label: "Unidad de medida", editable: false },
    {
        key: "estado",
        label: "Estado",
        render: (row) => {
            let variant = "default"; // Valor por defecto
            let estadoLegible = estadoLabels[row.estado] || "Sin estado";

            // Primero manejar los estados de modificación
            if (row.estadoModificacion === 3) {
                estadoLegible = "Descatalogado";
                variant = "danger";
            } else if (row.estadoModificacion === 4) {
                estadoLegible = "Nuevo";
                variant = "success";
            } else if (row.estadoModificacion === 2) {
                estadoLegible = "Modificado";
                variant = "success";
            } else {
                // Si no hay estadoModificacion especial, usar el estado normal
                switch (row.estado) {
                    case "Publicado":
                        variant = "success";
                        break;
                    case "Borrador":
                        variant = "warning";
                        break;
                    case "Archivado":
                        variant = "info";
                        break;
                    case "PendienteDeAprobacion":
                        variant = "info";
                        break;
                    case "EnEdicion":
                        variant = "success";
                        break;
                    case "ListoParaPublicar":
                        variant = "success";
                        break;
                    default:
                        variant = "default";
                }
            }

            return <Badge label={estadoLegible} variant={variant} />;
        },
    },
];

const estados = [
    "Publicado",
    "Borrador",
    "Archivado",
    "Pendiente de Aprobación",
    "Sin estado",
];

const estadoDefault = "Sin estado";

export default function SolicitudTarifas() {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedMuebles, setSelectedMuebles] = useState([]);
    const [products, setProducts] = useState([]);
    const [filterVariantes, setFilterVariantes] = useState([]);
    const [filterCategoria, setFilterCategorias] = useState([]);
    const [filterSubcategoria, setFilterSubcategoria] = useState([]);
    const [filterEstado, setFilterEstado] = useState([]);
    const [filterFamilia, setFilterFamilia] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [proveedores, setProveedores] = useState([]);
    const [selectedProveedores, setSelectedProveedores] = useState([]);
    const [isSolicitudTarifaModalOpen, setIsSolicitudTarifaModalOpen] =
        useState(false);
    const [plazo, setPlazo] = useState(15);
    const [nombreTarifa, setNombreTarifa] = useState("");
    const [variantes, setVariantes] = useState([]);
    const [subCategorias, setSubcategorias] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [user, setUser] = useState(null);
    const [tarifas, setTarifas] = useState([]);

    const notificationRef = useRef();

    const api = getApiInstance();

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const api = getApiInstance();
                const response = await api.get("/api/Usuario/GetCurrentUser");
                setUser(response.data);
            } catch (error) {
                console.error("Error al obtener el usuario:", error);
            }
        };

        fetchUser();
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await api.get("/api/Mueble/GetAllMuebles");
            setProducts(response.data || []);
        } catch (error) {
            console.error("Error al obtener los muebles:", error);
            setProducts([]);
        }
    };

    const fetchProveedores = async () => {
        try {
            const response = await api.get("/api/Empresa/GetAllEmpresas");
            const proveedoresFiltrados =
                response.data?.filter(
                    (empresa) => empresa.tipoEmpresa === "Proveedor"
                ) || [];

            setProveedores(proveedoresFiltrados);
            setSelectedProveedores(
                proveedoresFiltrados.map((proveedor) => proveedor.codEmpresa)
            );
        } catch (error) {
            console.error("Error al obtener los proveedores:", error);
            setProveedores([]);
        }
    };

    const fetchTarifas = async () => {
        try {
            const response = await api.get("/api/MueblesPrecios/Tarifas");

            setTarifas(
                response.data.map((tarifa) => ({
                    label: tarifa,
                    value: tarifa,
                }))
            );
        } catch (error) {
            console.error("Error al obtener los proveedores:", error);
            setTarifas([]);
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchTarifas();
    }, []);

    useEffect(() => {
        if (isSolicitudTarifaModalOpen) {
            fetchProveedores();
        }
    }, [isSolicitudTarifaModalOpen]);

    const filteredProducts = useMemo(() => {
        let filtered = products.filter((product) =>
            matchesSearchInObject(product, searchTerm)
        );

        return filtered
            .filter(
                (product) =>
                    filterVariantes.length === 0 ||
                    filterVariantes.includes(product.variante)
            )
            .filter(
                (product) =>
                    filterCategoria.length === 0 ||
                    filterCategoria.includes(familiaMapping[product.codFamilia])
            )
            .filter(
                (product) =>
                    filterSubcategoria.length === 0 ||
                    product.categorias.some((categoria) =>
                        filterSubcategoria.includes(categoria)
                    )
            )
            .filter((product) => {
                const estadoActual = estados.includes(product.estado)
                    ? product.estado
                    : estadoDefault;
                return (
                    filterEstado.length === 0 ||
                    filterEstado.includes(estadoActual)
                );
            })
            .filter(
                (product) =>
                    filterFamilia.length === 0 ||
                    filterFamilia.includes(product.nombreFamilia)
            );
    }, [
        searchTerm,
        products,
        filterVariantes,
        filterCategoria,
        filterSubcategoria,
        filterEstado,
        filterFamilia,
    ]);

    const handleRowSelect = (selectedRows) => {
        setSelectedMuebles(selectedRows);
    };

    const actionsTarifas = (row) => [{}];

    const openSolicitudTarifaModal = () => {
        if (selectedMuebles.length === 0) {
            notificationRef.current.addNotification(
                "Debes seleccionar al menos un mueble para solicitar tarifas.",
                "warning"
            );
            return;
        }
        setIsSolicitudTarifaModalOpen(true);
    };

    const getUsuariosDeProveedores = async (codsEmpresas) => {
        try {
            const response = await api.post(
                "/api/Usuario/GetUsuariosByCodEmpresas",
                codsEmpresas
            );
            return response.data || [];
        } catch (error) {
            console.error(
                "Error al obtener los usuarios de los proveedores:",
                error
            );
            return [];
        }
    };

    const closeSolicitudTarifaModal = () => {
        setIsSolicitudTarifaModalOpen(false);
    };

    const handleProveedorChange = (codEmpresa) => {
        setSelectedProveedores((prev) =>
            prev.includes(codEmpresa)
                ? prev.filter((id) => id !== codEmpresa)
                : [...prev, codEmpresa]
        );
    };

    const handleSendSolicitudTarifa = async () => {
        try {
            const response = await api.post(
                "/api/MueblesPrecios/CreateMueblePrecio",
                {
                    CodsMueble: selectedMuebles,
                    CodsProveedor: selectedProveedores,
                    Plazo: plazo || 15,
                    nombreTarifa: nombreTarifa,
                }
            );

            if (response.status === 200) {
                notificationRef.current.addNotification(
                    `Tarifa${selectedMuebles.length > 1 ? "s" : ""} enviada${
                        selectedMuebles.length > 1 ? "s" : ""
                    } correctamente`,
                    "success"
                );

                //Notificar a proveedores
                const usuarios = await getUsuariosDeProveedores(
                    selectedProveedores
                );
                const codUsuariosNotificados = usuarios.map(
                    (u) => u.codUsuario
                );

                const formData = new FormData();
                formData.append("DestinatariosNotificacion", 3);
                formData.append("CodUsuarioNotifica", user.codUsuario);
                formData.append(
                    "Mensaje",
                    `Se ha enviado una solicitud de tarifa para ${
                        selectedMuebles.length
                    } mueble${selectedMuebles.length > 1 ? "s" : ""}.`
                );
                formData.append("Titulo", "Solicitud de tarifa");
                formData.append("TipoNotificacion", 0);
                formData.append("Enlace", "/admin/solicitudes-tarifas");

                codUsuariosNotificados.forEach((cod) => {
                    formData.append("CodsUsuariosNotificados", cod);
                });
                await api.post("/api/Notificaciones", formData);
            }
            closeSolicitudTarifaModal();
        } catch (error) {
            notificationRef.current.addNotification(
                `Ha ocurrido un error al enviar la${
                    selectedMuebles.length > 1 ? "s" : ""
                } tarifa${selectedMuebles.length > 1 ? "s" : ""}`,
                "error"
            );
        }
    };

    const handleSelectVariante = (varianteNames) => {
        setFilterVariantes((prevSelected) => {
            let updatedSelection = [...prevSelected];
            varianteNames.forEach((variante) => {
                if (updatedSelection.includes(variante)) {
                    updatedSelection = updatedSelection.filter(
                        (v) => v !== variante
                    );
                } else {
                    updatedSelection.push(variante);
                }
            });
            return updatedSelection;
        });
    };

    const handleSelectCategoria = (categoriaName) => {
        setFilterCategorias((prevSelected) => {
            // Verificar si la categoria ya está en la lista
            if (prevSelected.includes(categoriaName)) {
                // Si ya está en la lista, eliminarla
                return prevSelected.filter(
                    (categoria) => categoria !== categoriaName
                );
            } else {
                // Si no está, agregarla
                return [...prevSelected, categoriaName];
            }
        });
    };

    const handleSelectSubcategoria = (subcategoriaName) => {
        setFilterSubcategoria((prevSelected) => {
            // Verificar si la subcategoria ya está en la lista
            if (prevSelected.includes(subcategoriaName)) {
                // Si ya está en la lista, eliminarla
                return prevSelected.filter(
                    (subcategoria) => subcategoria !== subcategoriaName
                );
            } else {
                // Si no está, agregarla
                return [...prevSelected, subcategoriaName];
            }
        });
    };

    const handleSelectEstado = (estadoName) => {
        setFilterEstado((prevSelected) => {
            // Verificar si el estado ya está en la lista
            if (prevSelected.includes(estadoName)) {
                // Si ya está en la lista, eliminarla
                return prevSelected.filter((estado) => estado !== estadoName);
            } else {
                // Si no está, agregarla
                return [...prevSelected, estadoName];
            }
        });
    };

    const handleSelectFamiliaFilter = (familiaName) => {
        setFilterFamilia((prevSelected) => {
            // Verificar si la familia ya está en la lista
            if (prevSelected.includes(familiaName)) {
                // Si ya está en la lista, eliminarla
                return prevSelected.filter(
                    (familia) => familia !== familiaName
                );
            } else {
                // Si no está, agregarla
                return [...prevSelected, familiaName];
            }
        });
    };

    const handleCleanFilter = () => {
        setFilterVariantes([]);
        setFilterCategorias([]);
        setFilterSubcategoria([]);
        setFilterEstado([]);
        setFilterFamilia([]);
    };

    useEffect(() => {
        const allVariantes = products
            .map((product) => product.variante) // Extraer variantes de cada producto
            .flat() // Aplanar el array (porque variantes es un array dentro de cada producto)
            .filter((value, index, self) => self.indexOf(value) === index); // Filtrar duplicados

        // Filtrar subcategorías sin duplicados
        const allSubcategorias = products
            .map((product) => product.categorias)
            .flat()
            .filter((value, index, self) => self.indexOf(value) === index);

        // Filtrar categorías sin duplicados
        const allCategorias = products
            .map((product) => familiaMapping[product.codFamilia])
            .filter((value, index, self) => self.indexOf(value) === index);

        const allFamilias = products
            .map((product) => product.nombreFamilia)
            .filter(
                (value, index, self) =>
                    self.indexOf(value) === index && value != null
            );

        // Actualizar el estado
        setVariantes(allVariantes);
        setSubcategorias(allSubcategorias);
        setCategorias(allCategorias);
    }, [products]);

    const handleSetNombreTarifa = (e, type = "select") => {
        // 1) Selección de un elemento existente: tipo "select"
        if (type === "select") {
            setNombreTarifa(e); // no cambiamos nada
            return;
        }

        // 3) Añadir uno nuevo: tipo "add"
        if (type === "add") {
            // formatear fecha actual YYYYMMDD
            const fecha = new Date()
                .toISOString()
                .slice(0, 10)
                .replace(/-/g, "");
                if (e.endsWith("_")) {
                    e = e.slice(0, -1).split("_")[0];
                }
            const newName = `${e}_${fecha}`;

            // 3a) Actualizamos el dropdown (suponiendo que tarifas viene de un state)
            setTarifas((old) => [...old, { label: newName, value: newName }]);

            // 3b) Seleccionamos ese nuevo
            setNombreTarifa(newName);
            return;
        }
    };

    return (
        <section className="dashboard-usuarios">
            <div className="dashboard-usuarios__content__table-header">
                <InputSearch
                    context="tableSearch"
                    placeholder="Buscar..."
                    onSearch={setSearchTerm}
                />
                <div className="dashboard-productos__content__table-header__buttons">
                    <Button
                        variant="outline"
                        label="Filtros"
                        icon={<FiFilter />}
                        onClick={() => setIsFiltersModalOpen(true)}
                    />

                    <Button
                        label="Solicitar tarifa"
                        variant="filled"
                        onClick={openSolicitudTarifaModal}
                    />
                </div>
            </div>

            <Table
                data={filteredProducts}
                columns={columns}
                selectedItems={selectedMuebles}
                onRowSelect={handleRowSelect}
            />

            <Modal
                title="Filtros"
                isOpen={isFiltersModalOpen}
                onClose={() => setIsFiltersModalOpen(false)}
                onAccept={() => setIsFiltersModalOpen(false)}
                outlineButtonLabel="Limpiar"
                primaryButtonLabel={`Ver resultados (${filteredProducts.length})`}
                onCancel={handleCleanFilter}
            >
                <div className="section-header__modal__section">
                    <InputDropdown
                        label="Variante"
                        options={variantes.map((variante) => {
                            return { label: variante, value: variante };
                        })}
                        onSelect={(option) => handleSelectVariante(option)}
                        withCheckboxes={true}
                        hasSearch={true}
                    />
                </div>
                <div className="section-header__modal__section">
                    <Accordion title="Categoría">
                        <ul className="section-header__modal__section__content">
                            {categorias.map((categoria, index) => (
                                <Tag
                                    key={index}
                                    label={categoria}
                                    isSelected={filterCategoria.includes(
                                        categoria
                                    )}
                                    onSelect={handleSelectCategoria}
                                />
                            ))}
                        </ul>
                    </Accordion>
                </div>
                <div className="section-header__modal__section">
                    <Accordion title="Subcategoría">
                        <ul className="section-header__modal__section__content">
                            {subCategorias.map((subcategoria, index) => (
                                <Tag
                                    key={index}
                                    label={subcategoria}
                                    isSelected={filterSubcategoria.includes(
                                        subcategoria
                                    )}
                                    onSelect={handleSelectSubcategoria}
                                />
                            ))}
                        </ul>
                    </Accordion>
                </div>
                <div className="section-header__modal__section">
                    <Accordion title="Estado">
                        <ul className="section-header__modal__section__content">
                            {estados.map((estado, index) => (
                                <Tag
                                    key={index}
                                    label={estado}
                                    isSelected={filterEstado.includes(estado)}
                                    onSelect={handleSelectEstado}
                                />
                            ))}
                        </ul>
                    </Accordion>
                </div>
            </Modal>

            <Modal
                title="Solicitar tarifa"
                isOpen={isSolicitudTarifaModalOpen}
                onClose={closeSolicitudTarifaModal}
                outlineButtonLabel="Cancelar"
                primaryButtonLabel="Enviar solicitud"
                onAccept={handleSendSolicitudTarifa}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                    }}
                >
                    <h2 style={{ marginBottom: "16px" }}>
                        Lista de proveedores disponibles:
                    </h2>
                    {proveedores.map((proveedor) => (
                        <InputCheckbox
                            key={proveedor.codEmpresa}
                            label={proveedor.nombre}
                            checked={selectedProveedores.includes(
                                proveedor.codEmpresa
                            )}
                            onChange={() =>
                                handleProveedorChange(proveedor.codEmpresa)
                            }
                        />
                    ))}
                    <InputDropdownAdd
                        options={tarifas}
                        value={nombreTarifa}
                        onSelect={(e) =>
                            handleSetNombreTarifa(e.value, "select")
                        }
                        onAddOption={(e) => handleSetNombreTarifa(e, "add")}
                        placeholder="Nombre Tarifa"
                        label="Nombre Tarifa"
                    />
                    <Input
                        key="plazo"
                        label="Plazo de entrega (días)"
                        type="number"
                        value={plazo}
                        onChange={(e) => setPlazo(e.target.value)}
                    />
                </div>
            </Modal>
            <Notification ref={notificationRef} />
        </section>
    );
}
